import { AbsenceOrigin } from '@bp/bp-graphql-types';
import { ProfileRoles } from '@bp/pim-auth-constants';
import {
  Checkbox,
  DatePicker,
  Grid,
  GridColumn,
  GridRow,
  Input,
  ModalBottomButtons,
  Select,
  type SelectOptionType,
} from '@bp/ui-components';
import { graphql } from 'client/gql';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import dayjs from 'dayjs';
import { Form, Formik, type FormikHelpers } from 'formik';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useCreateSelectOptions } from 'hooks/useCreateSelectOptions';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { type ChangeEvent, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { isAfter, isBefore, setTimeOnDate } from 'utils/dateCalculations';
import { naturalStringSort } from 'utils/stringHelper';
import { showSuccessCreateToast, showSuccessUpdateToast, showUserDbErrorToast } from 'utils/toasts';
import { CONNECT_DATE_TEMPLATE, connectDays } from '../../../utils/connectLib';
import { onlyUnique } from '../../../utils/uniquByUuid';
import { AbsenceContext, MyAbsenceContext } from '../types/absenceTypes';

const CreateAbsenceMutationDocument = graphql(`
  mutation BpCreateAbsenceInAbsencesForm($input: [AbsenceCreateInput!]!) {
    createAbsences(input: $input) {
      absences {
        __typename
        uuid
      }
    }
  }
`);

export const UpdateAbsenceMutationDocument = graphql(`
  mutation BpUpdateAbsence($where: AbsenceWhere!, $update: AbsenceUpdateInput!) {
    updateAbsences(where: $where, update: $update) {
      absences {
        __typename
        uuid
        startTime
        endTime
        excused
        reason {
          __typename
          uuid
        }
      }
    }
  }
`);

const AbsenceFormQuery = graphql(`
  query AbsenceFormData(
    $absenceReasonsWhere: AbsenceReasonWhere!
    $absenceUuid: ID!
    $profilesWhere: ProfileWhere!
    $coursesWhere: GroupWhere!
    $classesWhere: ClassWhere!
    $schoolYearUuid: ID!
  ) {
    absences(where: { uuid: $absenceUuid }, options: { sort: { startTime: DESC } }) {
      __typename
      uuid
      owner {
        __typename
        uuid
      }
      reason {
        __typename
        uuid
      }
      startTime
      endTime
      comment
      excused
    }
    absenceReasons(where: $absenceReasonsWhere) {
      __typename
      uuid
      reason
    }
    profiles(where: $profilesWhere) {
      __typename
      uuid
      organizationRoles
      listName
      classes(where: { schoolYear: { uuid: $schoolYearUuid } }) {
        uuid
        name
      }
    }
    courses: groups(where: $coursesWhere, options: { sort: { name: ASC } }) {
      __typename
      uuid
      name
      editors {
        __typename
        uuid
      }
      viewers {
        __typename
        uuid
      }
    }
    classes(where: $classesWhere, options: { sort: { name: ASC } }) {
      __typename
      uuid
      name
      membersConnection {
        __typename
        edges {
          __typename
          node {
            __typename
            uuid
          }
        }
      }
    }
  }
`);

type AbsenceFormProps = {
  context: AbsenceContext | MyAbsenceContext;
  selectedAbsenceUuid: string | null;
  onLoad: (isLoading: boolean) => void;
  onClose: () => void;
};

type AbsenceFormType = {
  ownerUuid: string;
  reasonUuid: string;
  start: Date;
  end: Date;
  startTimeError: string; // only used for error messages
  endTimeError: string; // only used for error messages
  comment: string;
  isExcused: boolean;
};

export const AbsenceForm = ({ context, selectedAbsenceUuid, onLoad, onClose }: AbsenceFormProps) => {
  const { t } = useTranslation();
  const { currentSchoolYear } = useContext(OrganizationConfigContext);
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganization().uuid;
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const children = pimAuthClaims.getChildProfiles();
  const isAdminContext = context === 'admin';
  const isTeacherContext = context === 'teacher';
  const isSelfContext = context === 'myself';
  const isChildContext = context === 'child';

  const [{ data }] = useQuery({
    query: AbsenceFormQuery,
    variables: {
      absenceUuid: selectedAbsenceUuid ?? '',
      absenceReasonsWhere: {
        organization: { uuid: organizationUuid },
        internal: isAdminContext ? undefined : false,
      },
      profilesWhere: {
        organization: { uuid: organizationUuid },
      },
      schoolYearUuid: currentSchoolYear.uuid,
      coursesWhere: {
        ...(isTeacherContext
          ? {
              editors_SOME: { uuid: profileUuid },
            }
          : undefined),
        schoolYear: {
          uuid: currentSchoolYear.uuid,
        },
      },
      classesWhere: {
        organization: { uuid: organizationUuid },
        ...(isTeacherContext
          ? {
              tutorsConnection_SOME: {
                node: {
                  uuid: profileUuid,
                },
              },
            }
          : undefined),
        schoolYear: {
          uuid: currentSchoolYear.uuid,
        },
      },
    },
  });

  const absencesData = data?.absences ?? [];
  const reasonsData = data?.absenceReasons ?? [];
  const profileData = data?.profiles ?? [];
  const coursesData = data?.courses ?? [];
  const classesData = data?.classes ?? [];

  const permissions = usePermissionChecker();

  const [, createAbsence] = useMutation(CreateAbsenceMutationDocument);
  const [, updateAbsence] = useMutation(UpdateAbsenceMutationDocument);

  const absence = absencesData?.find((absence) => absence.uuid === selectedAbsenceUuid);

  const startsInFuture = dayjs(absence?.startTime).startOf('day').isSameOrAfter(dayjs().startOf('day'));
  const startDisabled = absence ? !startsInFuture : false;

  const childOptions = useCreateSelectOptions(children, 'uuid', 'displayName');
  const reasonOptions = useCreateSelectOptions(reasonsData, 'uuid', 'reason');

  const adminOptions: SelectOptionType[] = useMemo(() => {
    const options: SelectOptionType[] = [];
    const profiles = profileData?.filter((p) => p.organizationRoles.includes(ProfileRoles.Student));

    if (isAdminContext) {
      profiles.forEach((p) => {
        options.push({
          value: p.uuid,
          label: p.listName ?? '',
        });
      });
    } else {
      profiles?.forEach((p) => {
        coursesData?.forEach((c) => {
          const editorUuids = c.editors.map((e) => e.uuid);
          const viewerUuids = c.viewers.map((v) => v.uuid);
          if ([...editorUuids, ...viewerUuids].includes(p.uuid)) {
            options.push({
              value: p.uuid,
              label: p.listName ?? '',
              group:
                p.classes
                  ?.map((cl) => cl.name)
                  .filter(onlyUnique)
                  .sort((a, b) => naturalStringSort(a, b))
                  .join(', ') ?? '',
            });
          }
        });

        classesData?.forEach((c) => {
          if (c.membersConnection.edges.some((e) => e.node.uuid === p.uuid)) {
            options.push({
              value: p.uuid,
              label: p.listName ?? '',
              group: c.name,
            });
          }
        });
      });
    }

    // make unique by value, prefer entries with a defined group
    const uniqueMap = new Map<string, SelectOptionType>();
    options.forEach((option) => {
      const key = String(option.value);
      const existing = uniqueMap.get(key);
      if (!existing) {
        uniqueMap.set(key, option);
      } else {
        const existingHasGroup = existing.group !== undefined && existing.group !== '';
        const newHasGroup = option.group !== undefined && option.group !== '';
        if (!existingHasGroup && newHasGroup) {
          uniqueMap.set(key, option);
        }
      }
    });

    return Array.from(uniqueMap.values())
      .map((option) => ({
        value: option.value,
        label: `${option.label}${option.group ? ` (${option.group})` : ``}`,
      }))
      .sort((a, b) => naturalStringSort(a.label ?? '', b.label ?? ''));
  }, [isAdminContext, coursesData, classesData, profileData]);

  const canExcuse = isAdminContext || !!permissions?.canExcuseChildren();

  const initialValues: AbsenceFormType = {
    ownerUuid: absence
      ? absence.owner.uuid
      : isChildContext && children.length === 1
        ? (childOptions[0].value as string)
        : '',
    reasonUuid: absence?.reason?.uuid ?? '',
    start: absence?.startTime ?? (isSelfContext ? new Date() : dayjs().startOf('day').toDate()),
    end: absence?.endTime ?? dayjs().endOf('day').toDate(),
    startTimeError: '',
    endTimeError: '',
    comment: absence?.comment ?? '',
    isExcused: absence?.excused ?? canExcuse,
  };

  async function handleSubmit(values: AbsenceFormType, formHelpers: FormikHelpers<AbsenceFormType>) {
    let hasErrors = false;
    if (!isSelfContext && !values.ownerUuid) {
      formHelpers.setFieldError('ownerUuid', t('validation.required.profile'));
      hasErrors = true;
    }
    if (!values.reasonUuid) {
      formHelpers.setFieldError('reasonUuid', t('validation.required.reason'));
      hasErrors = true;
    }
    if (hasErrors) return;

    const ownerUuid = isSelfContext ? profileUuid : values.ownerUuid;

    const connectAbsence = {
      connect: { where: { node: { uuid: values.reasonUuid } } },
    };

    const connectOwner = { connect: { where: { node: { uuid: ownerUuid } } } };

    onLoad(true);

    if (!selectedAbsenceUuid) {
      const { error } = await createAbsence({
        input: {
          originUuid: profileUuid,
          origin: isAdminContext
            ? AbsenceOrigin.SchoolOffice
            : isSelfContext
              ? AbsenceOrigin.Self
              : AbsenceOrigin.Parent,
          foreignRefId: [],
          startTime: values.start,
          endTime: values.end,
          excused: values.isExcused,
          reason: connectAbsence,
          comment: values.comment,
          owner: connectOwner,
          days: connectDays(
            dayjs()
              .recur(dayjs(values.start), dayjs(values.end))
              .every(1, 'days')
              .all()
              .map((d) => d.format(CONNECT_DATE_TEMPLATE)),
          ),
        },
      });

      if (error) {
        showUserDbErrorToast();
      } else {
        showSuccessCreateToast();
      }
    } else {
      const { error } = await updateAbsence({
        where: {
          uuid: selectedAbsenceUuid,
        },
        update: {
          startTime: values.start,
          endTime: values.end,
          excused: values.isExcused,
          reason: {
            disconnect: {},
            ...connectAbsence,
          },
          days: [
            {
              disconnect: [{}],
              ...connectDays(
                dayjs()
                  .recur(dayjs(values.start), dayjs(values.end))
                  .every(1, 'days')
                  .all()
                  .map((d) => d.format(CONNECT_DATE_TEMPLATE)),
              ),
            },
          ],
          comment: values.comment,
        },
      });

      if (error) {
        showUserDbErrorToast();
      } else {
        showSuccessUpdateToast();
      }
    }

    onLoad(false);
    formHelpers.resetForm();
    onClose();
  }

  return (
    <Formik<AbsenceFormType> initialValues={initialValues} onSubmit={handleSubmit}>
      {({ setFieldValue, values, isSubmitting, dirty, resetForm, errors, setFieldError }) => {
        return (
          <Form>
            <Grid useFormGap>
              <GridRow>
                <GridColumn width={10}>
                  <GridRow spacingBottom='s'>
                    {isChildContext && (
                      <GridColumn width={6}>
                        <Select
                          label={t('absences.forChild')}
                          isSearchable
                          options={childOptions}
                          onChange={async (e) => {
                            const childUuid = e && ((e as SelectOptionType).value as number);
                            await setFieldValue('ownerUuid', childUuid);
                          }}
                          value={childOptions.find((c) => c.value === values.ownerUuid)}
                          readonly={children.length === 1}
                          disabled={children.length === 0}
                          name='child'
                          error={errors.ownerUuid}
                        />
                      </GridColumn>
                    )}
                    {(isAdminContext || isTeacherContext) && (
                      <GridColumn width={6}>
                        <Select
                          label={t('absences.forStudent')}
                          isSearchable
                          options={adminOptions}
                          onChange={async (e) => {
                            const profileUuid = e && ((e as SelectOptionType).value as number);
                            await setFieldValue('ownerUuid', profileUuid);
                          }}
                          value={adminOptions.find((c) => c.value === values.ownerUuid)}
                          name='profile'
                          error={errors.ownerUuid}
                        />
                      </GridColumn>
                    )}
                    <GridColumn width={isSelfContext ? 12 : 6}>
                      <Select
                        label={t('absences.reasons.title', { context: 'short' })}
                        options={reasonOptions}
                        onChange={async (e) => {
                          const reasonUuid = e && ((e as SelectOptionType).value as number);
                          await setFieldValue('reasonUuid', reasonUuid);
                        }}
                        value={reasonOptions.find((r) => r.value === values.reasonUuid)}
                        name='reason'
                        error={errors.reasonUuid}
                      />
                    </GridColumn>
                  </GridRow>
                  <GridRow spacingTop='s' spacingBottom='s'>
                    <GridColumn width={3}>
                      <DatePicker
                        name='start-date'
                        value={values.start}
                        label={t('common.from')}
                        disabled={startDisabled}
                        onChange={async (e) => {
                          await setFieldValue('start', e, false);
                          if (isSelfContext && isBefore(e, new Date(), 'minute')) {
                            setFieldError('startTimeError', t('validation.time.startTimeBeforeNow'));
                          } else if (isAfter(e, values.end)) {
                            setFieldError('start', t('validation.time.startDateAfterEnd'));
                          } else if (isAfter(e, values.end, 'minutes')) {
                            setFieldError('startTimeError', t('validation.time.startTimeAfterEnd'));
                          } else {
                            setFieldError('start', undefined);
                            setFieldError('startTimeError', undefined);
                            setFieldError('end', undefined);
                            setFieldError('endTimeError', undefined);
                          }
                        }}
                        minDate={isSelfContext ? dayjs().toDate() : dayjs(currentSchoolYear?.start).toDate()}
                        maxDate={dayjs(currentSchoolYear?.end).toDate()}
                        error={errors.start as string}
                      />
                    </GridColumn>
                    <GridColumn width={3}>
                      <Input
                        name='start-time'
                        disabled={startDisabled}
                        label={t('common.start')}
                        value={dayjs(values.start).format('HH:mm')}
                        onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                          const time = setTimeOnDate(values.start, e.target.value);
                          await setFieldValue('start', time, false);
                          if (isSelfContext && isBefore(time, new Date(), 'minute')) {
                            setFieldError('startTimeError', t('validation.time.startTimeBeforeNow'));
                          } else if (isAfter(time, values.end, 'minutes')) {
                            setFieldError('startTimeError', t('validation.time.startTimeAfterEnd'));
                          } else if (isAfter(time, values.end)) {
                            setFieldError('start', t('validation.time.startDateAfterEnd'));
                          } else {
                            setFieldError('start', undefined);
                            setFieldError('startTimeError', undefined);
                            setFieldError('end', undefined);
                            setFieldError('endTimeError', undefined);
                          }
                        }}
                        error={errors.startTimeError as string}
                        type='time'
                      />
                    </GridColumn>
                    <GridColumn width={3}>
                      <DatePicker
                        name='end-date'
                        value={values.end}
                        label={t('common.until')}
                        onChange={async (e) => {
                          await setFieldValue('end', e);
                          if (isBefore(e, values.start)) {
                            setFieldError('end', t('validation.time.endDateBeforeStart'));
                          } else if (isBefore(e, values.start, 'minutes')) {
                            setFieldError('endTimeError', t('validation.time.endTimeBeforeEnd'));
                          } else {
                            setFieldError('start', undefined);
                            setFieldError('startTimeError', undefined);
                            setFieldError('end', undefined);
                            setFieldError('endTimeError', undefined);
                          }
                        }}
                        minDate={dayjs(currentSchoolYear?.start).toDate()}
                        maxDate={dayjs(currentSchoolYear?.end).toDate()}
                        error={errors.end as string}
                      />
                    </GridColumn>
                    <GridColumn width={3}>
                      <Input
                        name='end-time'
                        label={t('common.end')}
                        value={dayjs(values.end).format('HH:mm')}
                        onChange={async (event: ChangeEvent<HTMLInputElement>) => {
                          const time = setTimeOnDate(values.end, event.target.value);
                          await setFieldValue('end', time);
                          if (isBefore(time, values.start, 'minutes')) {
                            setFieldError('endTimeError', t('validation.time.endTimeBeforeEnd'));
                          } else if (isBefore(time, values.start)) {
                            setFieldError('end', t('validation.time.endDateBeforeStart'));
                          } else {
                            setFieldError('start', undefined);
                            setFieldError('startTimeError', undefined);
                            setFieldError('end', undefined);
                            setFieldError('endTimeError', undefined);
                          }
                        }}
                        error={errors.endTimeError as string}
                        type='time'
                      />
                    </GridColumn>
                  </GridRow>
                  <GridRow spacingTop='s'>
                    <Input
                      label={t('common.comment')}
                      name='comment'
                      value={values.comment}
                      onChange={async (e) => await setFieldValue('comment', e.target.value)}
                    />
                  </GridRow>
                </GridColumn>
                <GridColumn width={2} align='end'>
                  <Checkbox
                    className='mt-3'
                    label={t('absences.isExcused')}
                    name='is-excused'
                    onChange={async (e) => await setFieldValue('isExcused', e.target.checked)}
                    checked={values.isExcused}
                    disabled={!canExcuse}
                  />
                </GridColumn>
              </GridRow>
            </Grid>

            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  onClose();
                  resetForm();
                },
              }}
              submitButton={{
                disabled: isSubmitting || !dirty,
              }}
              errors={errors}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
